import { useCallback, useEffect, useRef, useState } from "react";
import api from "./api";

/**
 * Feb 2026 · User-Directive „Admin-Badge auf Angebote-Nav" (3 Sep 2026).
 *
 * useUnreadTeamQuotes — pollt für den eingeloggten Team-User die Anzahl
 * der noch nicht geöffneten oder neu entschiedenen Angebote. Der Badge
 * an der Team-Sidebar-„Angebote"-Verknüpfung nutzt diesen Zähler.
 *
 * Endpoint: `GET /api/quotes/team/unread-count` → `{count: number}`
 *
 * Poll-Intervall: 20 s. Zusätzlich sofortiges Reload beim Tab-Focus und
 * beim benutzerdefinierten Event `weissblom:unread-team-quotes-refresh`
 * (feuert der `TeamQuotes`-Screen, sobald der Admin ein Angebot öffnet).
 */
const EVT = "weissblom:unread-team-quotes-refresh";

export function bumpUnreadTeamQuotes() {
  try { window.dispatchEvent(new CustomEvent(EVT)); } catch { /* ignore */ }
}

export function useUnreadTeamQuotes(enabled) {
  const [total, setTotal] = useState(0);
  const alive = useRef(true);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await api.get("/quotes/team/unread-count");
      if (!alive.current) return;
      setTotal(Number(data?.count || 0));
    } catch { /* silent — badge just won't update this cycle */ }
  }, [enabled]);

  useEffect(() => {
    alive.current = true;
    if (!enabled) return () => { alive.current = false; };
    load();
    const iv = setInterval(load, 20000);
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    const onBump = () => load();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(EVT, onBump);
    return () => {
      alive.current = false;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(EVT, onBump);
    };
  }, [enabled, load]);

  return { total, refresh: load };
}
